<script setup lang="ts">
const { user, login } = useOidcAuth();

const { $fetchAPI } = useNuxtApp();

const props = defineProps<{
  themeKeys: {
    bg: string;
    title: string;
    description: string;
    price: string;
    ctaBg: string;
    ctaFg: string;
  };
  cta: string;
  customPrice: string;
  quantityDescription: string;
  priceDescription: string;
}>();

const selectedPriceUnit = ref<string>('CAD');

// prices from stripe
const { data: stripePrices, status: stripePricesStatus } = await useAsyncData(
  async () => $fetch('/api/stripe/public/prices'),
);

type PriceOption = {
  id: string;
  quantity: number;
  pricePerBook: number;
  currency: string;
};

const options = computed(() =>
  (stripePrices.value || [])
    .map((price) => {
      const quantity = price.transform_quantity?.divide_by ?? 1;
      return {
        id: price.id,
        quantity,
        pricePerBook: Math.round(price.unit_amount / 100 / quantity),
        currency: price.currency.toUpperCase(),
      };
    })
    .sort((a, b) => a.quantity - b.quantity),
);

// wants to buy
const handleWantsToBuy = async (option: PriceOption) => {
  if (user.value) {
    // Call is okay because user is logged in
    const sessionUrl = await $fetchAPI('/api/stripe/createCheckoutSession', {
      method: 'POST',
      body: { quantity: option.quantity, referralId: null },
    });

    navigateTo(sessionUrl, { external: true });
  } else {
    login();
  }
};
</script>

<template>
  <div
    class="outer-p-container"
    :style="{
      backgroundColor: props.themeKeys.bg,
      color: props.themeKeys.title
    }"
  >
    <div class="inner-container">
      <div class="flex flex-col justify-center gap-8 md:w-4/5 mx-auto">
        <div
          v-for="option in options"
          class="flex flex-row justify-between gap-8"
        >
          <div class="space-x-2">
            <span
              class="font-semibold text-4xl md:text-5xl"
              :style="{ color: props.themeKeys.title }"
            >
              {{ option.quantity }}
            </span>
            <span
              class="font-semibold text-2xl md:text-3xl"
              :style="{ color: props.themeKeys.title }"
            >
              {{ pluralize(props.quantityDescription, option.quantity) }}
            </span>
          </div>
          <div class="flex flex-row items-center gap-2">
            <div class="flex flex-col justify-between items-end mr-6">
              <div class="text-end space-x-2">
                <span
                  class="text-4xl md:text-5xl font-semibold"
                  :style="{ color: props.themeKeys.price }"
                >
                  {{ option.pricePerBook }}
                </span>
                <span
                  class="font-semibold text-2xl md:text-3xl"
                  :style="{ color: props.themeKeys.description }"
                >{{ option.currency }}</span>
              </div>
              <div>
                {{ props.priceDescription }}
              </div>
            </div>
            <UButton
              size="xl"
              class="rounded-full"
              :style="{
                backgroundColor: props.themeKeys.ctaBg,
                color: props.themeKeys.ctaFg
              }"
              @click="handleWantsToBuy(option)"
            >
              {{ props.cta }}
            </UButton>
          </div>
        </div>
        <div class="text-sm text-(--ui-text-toned) italic">
          <UIcon name="i-lucide-info" class="size-3 align-baseline" />
          {{ props.customPrice }}
        </div>
      </div>
    </div>
  </div>
</template>
